import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SignatureCanvas from "react-signature-canvas";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const SignatureCaptureModal = ({
  open,
  title,
  submitLabel,
  onSubmit,
  onClose,
  submitting,
  signatureRequirements = [],
  signatureRequirementsLoading = false,
}) => {
  const signatureRef = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open && signatureRef.current) {
      signatureRef.current.clear();
        setIsSigned(false);
        setError("");
    }
  }, [open]);

  const handleClose = () => {
    if (submitting) {
      return;
    }
    setError("");
    onClose && onClose();
  };

  const handleClear = () => {
    if (signatureRef.current) {
      signatureRef.current.clear();
    }
    setIsSigned(false);
    setError("");
  };

  const handleEnd = () => {
        if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setIsSigned(true);
      setError("");
    }
  };

  const handleSave = () => {
    if (!signatureRef.current || signatureRef.current.isEmpty()) {
      setError("Please provide a signature before saving.");
      setIsSigned(false);
      return;
    }
    const signatureUrl = signatureRef.current
      .getTrimmedCanvas()
      .toDataURL("image/png");
    onSubmit && onSubmit(signatureUrl);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
          color: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 2,
          px: { xs: 2, md: 3 },
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            onClick={handleClose}
            sx={{
              minWidth: "36px",
              height: "36px",
              color: "#ffffff",
              "&:hover": { color: "#e0e7ff" },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          background:
            "linear-gradient(180deg, #f8fafc 0%, rgba(248, 250, 252, 0.6) 100%)",
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        {error && (
          <Alert
            severity="warning"
            sx={{ mb: 3, borderRadius: 2 }}
            onClose={() => setError("")}
          >
            {error}
          </Alert>
        )}
        {(signatureRequirementsLoading || (signatureRequirements && signatureRequirements.length > 0)) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "purple.200",
              bgcolor: "rgba(147, 51, 234, 0.06)",
            }}
          >
            {signatureRequirementsLoading ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <CircularProgress size={20} />
                <Typography variant="body2" sx={{ color: "grey.700" }}>
                  Loading required forms…
                </Typography>
              </Box>
            ) : (
              <React.Fragment>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: "grey.900" }}>
                  Your pickup requires you to sign the following form(s). Please sign below.
                </Typography>
                <Typography variant="body2" sx={{ color: "grey.700", mb: 1 }}>
                  Required form(s):
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2.5, listStyle: "disc" }}>
                  {signatureRequirements.map((req, idx) => (
                <Box key={idx} component="li" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                  <Typography component="span" variant="body2">
                    {req.displayName || req.flagLabel}
                  </Typography>
                  {!req.missingForm && req.formUrl && (
                    <Typography
                      component="a"
                      href={req.formUrl.startsWith("http") ? req.formUrl : `${(process.env.REACT_APP_BASEURL || "").replace(/\/+$/, "")}${req.formUrl.startsWith("/") ? req.formUrl : `/${req.formUrl}`}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="body2"
                      sx={{ color: "purple.600", textDecoration: "underline" }}
                    >
                      View form
                    </Typography>
                  )}
                </Box>
              ))}
                </Box>
              </React.Fragment>
            )}
          </Box>
        )}
        <Box
          sx={{
            position: "relative",
            borderRadius: 3,
            border: isSigned ? "2px solid #16a34a" : "2px dashed #cbd5e1",
            backgroundColor: isSigned ? "#ecfdf5" : "#f8fafc",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
            transition: "all 0.3s ease",
            overflow: "hidden",
            width: "100%",
            aspectRatio: "800 / 220",
          }}
        >
          <SignatureCanvas
            ref={signatureRef}
            penColor="#1f2937"
          canvasProps={{
            width: 800,
            height: 220,
            style: {
              width: "100%",
              height: "100%",
              backgroundColor: "transparent",
            },
          }}
            onEnd={handleEnd}
            onBegin={() => setError("")}
          />
          {isSigned && (
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 0.75,
                borderRadius: "999px",
                backgroundColor: "#15803d",
                color: "#ffffff",
                fontWeight: 600,
                fontSize: "0.75rem",
                boxShadow: "0 10px 24px rgba(22, 163, 74, 0.4)",
              }}
            >
              <CheckCircleIcon fontSize="small" />
              Signed
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          backgroundColor: "#ffffff",
          borderTop: "1px solid #e2e8f0",
          display: "flex",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClear}
          sx={{
            textTransform: "none",
            borderRadius: 2,
            px: 3,
            fontWeight: 600,
            borderWidth: 2,
            borderColor: "#cbd5e1",
            color: "#475569",
            "&:hover": {
              borderColor: "#94a3b8",
              backgroundColor: "rgba(148, 163, 184, 0.1)",
            },
          }}
          disabled={submitting}
        >
          Clear Signature
        </Button>
        <Box sx={{ display: "flex", gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3,
              fontWeight: 600,
              borderWidth: 2,
              borderColor: "#cbd5e1",
              color: "#475569",
              "&:hover": {
                borderColor: "#94a3b8",
                backgroundColor: "rgba(148, 163, 184, 0.1)",
              },
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={submitting}
            sx={{
              textTransform: "none",
              borderRadius: 2,
              px: 3.5,
              fontWeight: 600,
              background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
              boxShadow: "0 10px 24px rgba(124, 58, 237, 0.35)",
              "&:hover": {
                background:
                  "linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)",
              },
              "&:disabled": {
                background: "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                opacity: 0.7,
              },
            }}
            startIcon={
              submitting ? (
                <CircularProgress size={16} sx={{ color: "#ffffff" }} />
              ) : null
            }
          >
            {submitting ? "Saving..." : submitLabel}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

SignatureCaptureModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  title: PropTypes.string,
  submitLabel: PropTypes.string,
  submitting: PropTypes.bool,
  signatureRequirements: PropTypes.arrayOf(
    PropTypes.shape({
      flagLabel: PropTypes.string,
      displayName: PropTypes.string,
      formUrl: PropTypes.string,
      missingForm: PropTypes.bool,
    })
  ),
  signatureRequirementsLoading: PropTypes.bool,
};

SignatureCaptureModal.defaultProps = {
  onClose: undefined,
  onSubmit: undefined,
  title: "Capture Signature",
  submitLabel: "Save Signature",
  submitting: false,
  signatureRequirements: [],
  signatureRequirementsLoading: false,
};

export default SignatureCaptureModal;





// WEBPACK FOOTER //
// ./src/components/assign-prescriptions/counter/dialogs/SignatureCaptureModal.jsx